import React, { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as campaignQuoteTemplateItemActions from '@redux/modules/campaignQuoteTemplateItem'

import defaultFormState, { requiredFields } from '@models/campaignQuoteTemplateItem'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type {
  CampaignQuoteTemplateItemModel,
  CampaignQuoteTemplateItemRequestOptions,
  CreateMultipleCampaignQuoteTemplateItemsPayload,
} from '@models/types'

const stringifyArrayKeys: (keyof CampaignQuoteTemplateItemModel)[] = ['options']
const stringifyObjectKeys: (keyof CampaignQuoteTemplateItemModel)[] = ['data']

export const generateUrls = (campaignQuoteTemplateItem?: Partial<CampaignQuoteTemplateItemModel>) => {
  const { id } = campaignQuoteTemplateItem || {}

  return {
    editCampaignQuoteTemplateItemDetailsUrl: `#/systemManager/campaignQuoteTemplateItems/${id}/edit/details`,
    editCampaignQuoteTemplateItemUrl: `#/systemManager/campaignQuoteTemplateItems/${id}/edit/`,
    campaignQuoteTemplateItemsIndexUrl: '#/systemManager/campaignQuoteTemplateItems',
  }
}

type CreateCampaignQuoteTemplateItemParams = {
  campaignQuoteTemplateItemParams: Partial<CampaignQuoteTemplateItemModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteTemplateItemRequestOptions,
}

const createCampaignQuoteTemplateItem = (params: CreateCampaignQuoteTemplateItemParams) => {
  const { dispatch, campaignQuoteTemplateItemParams, requestOptions } = params
  const { createCampaignQuoteTemplateItem: createFn } = campaignQuoteTemplateItemActions

  const updatedParams = mergeAndStringifyEntityObjects({}, campaignQuoteTemplateItemParams, stringifyObjectKeys, stringifyArrayKeys)

  return dispatch(createFn(updatedParams, requestOptions))
}

type CreateCampaignQuoteTemplateItemsParams = {
  dispatch: AppDispatch,
  itemPayload: CreateMultipleCampaignQuoteTemplateItemsPayload,
  orderType: 'bundle' | 'publication',
  requestOptions?: CampaignQuoteTemplateItemRequestOptions,
}

const createCampaignQuoteTemplateItems = (params: CreateCampaignQuoteTemplateItemsParams) => {
  const {
    dispatch, itemPayload, orderType, requestOptions,
  } = params
  const { createCampaignQuoteTemplateItems: createFn } = campaignQuoteTemplateItemActions

  // @ts-expect-error TS2345
  const updatedParams = mergeAndStringifyEntityObjects({}, itemPayload, stringifyObjectKeys, stringifyArrayKeys)

  return dispatch(createFn(orderType, updatedParams, requestOptions))
}

type UpdateCampaignQuoteTemplateItemParams = {
  campaignQuoteTemplateItem: CampaignQuoteTemplateItemModel,
  campaignQuoteTemplateItemParams: Partial<CampaignQuoteTemplateItemModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteTemplateItemRequestOptions,
}

const updateCampaignQuoteTemplateItem = (params: UpdateCampaignQuoteTemplateItemParams) => {
  const {
    dispatch, campaignQuoteTemplateItem, campaignQuoteTemplateItemParams, requestOptions,
  } = params
  const { updateCampaignQuoteTemplateItem: updateFn } = campaignQuoteTemplateItemActions

  const updatedParams = mergeAndStringifyEntityObjects(
    campaignQuoteTemplateItem,
    campaignQuoteTemplateItemParams,
    stringifyObjectKeys,
    stringifyArrayKeys,
  )

  return dispatch(updateFn(updatedParams, requestOptions))
}

type UpdateCampaignQuoteTemplateItemOptionsParams = {
  campaignQuoteTemplateItem: CampaignQuoteTemplateItemModel,
  itemPayload: Partial<CampaignQuoteTemplateItemModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteTemplateItemRequestOptions,
}

const updateCampaignQuoteTemplateItemOptions = (params: UpdateCampaignQuoteTemplateItemOptionsParams) => {
  const {
    dispatch, campaignQuoteTemplateItem, itemPayload, requestOptions,
  } = params
  const { updateCampaignQuoteTemplateItemQtyOptions: updateFn } = campaignQuoteTemplateItemActions

  const updatedParams = {
    ...itemPayload,
    options: typeof itemPayload.options === 'string' ? itemPayload.options : JSON.stringify(itemPayload.options),
  }

  // @ts-expect-error TS2345
  return dispatch(updateFn(campaignQuoteTemplateItem.id, updatedParams, requestOptions))
}

type DuplicateCampaignQuoteTemplateItemParams = {
  campaignQuoteTemplateItem: CampaignQuoteTemplateItemModel,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteTemplateItemRequestOptions,
}

const duplicateCampaignQuoteTemplateItem = (params: DuplicateCampaignQuoteTemplateItemParams) => {
  const {
    dispatch, campaignQuoteTemplateItem, requestOptions,
  } = params

  const { duplicateCampaignQuoteTemplateItem: duplicateFn } = campaignQuoteTemplateItemActions

  return dispatch(duplicateFn(campaignQuoteTemplateItem, requestOptions))
}

type DeleteCampaignQuoteTemplateItemParams = {
  campaignQuoteTemplateItem: DeleteParams<CampaignQuoteTemplateItemModel>,
  dispatch: AppDispatch,
}

const deleteCampaignQuoteTemplateItem = (params: DeleteCampaignQuoteTemplateItemParams) => {
  const { dispatch, campaignQuoteTemplateItem } = params
  const { deleteCampaignQuoteTemplateItem: deleteFn } = campaignQuoteTemplateItemActions

  return dispatch(deleteFn(campaignQuoteTemplateItem))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCampaignQuoteTemplateItemForm(
  campaignQuoteTemplateItem: Partial<CampaignQuoteTemplateItemModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const campaignQuoteTemplateItemForm = useForm(
    defaultFormState,
    { entity: campaignQuoteTemplateItem, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [campaignQuoteTemplateItem.id, campaignQuoteTemplateItem.cache_key],
  )

  return {
    ...campaignQuoteTemplateItemForm,
  }
}

export const useRelations = (campaignQuoteTemplateItem: Partial<CampaignQuoteTemplateItemModel> = {}) => {
  const {
    campaign_quote_template_id,
    campaign_quote_template_section_id,
  } = campaignQuoteTemplateItem

  const { campaignQuoteTemplates, campaignQuoteTemplateSections } = useSelector(reduxState => reduxState.entities)

  const campaignQuoteTemplate = campaign_quote_template_id && campaignQuoteTemplates[campaign_quote_template_id]
    ? campaignQuoteTemplates[campaign_quote_template_id]
    : {}

  const campaignQuoteTemplateSection = campaign_quote_template_section_id
    && campaignQuoteTemplateSections[campaign_quote_template_section_id]
    ? campaignQuoteTemplateSections[campaign_quote_template_section_id]
    : {}

  return {
    campaignQuoteTemplate,
    campaignQuoteTemplateSection,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: CampaignQuoteTemplateItemRequestOptions,
}

function useCampaignQuoteTemplateItem(initEntity: Partial<CampaignQuoteTemplateItemModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: campaignQuoteTemplateItem }: { entity: CampaignQuoteTemplateItemModel} = useLatestEntity(
    initEntity,
    'campaignQuoteTemplateItems',
  )
  const { id } = campaignQuoteTemplateItem

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { campaignQuoteTemplate } = useRelations(campaignQuoteTemplateItem)

  useReduxAction(
    'campaignQuoteTemplateItems',
    'loadCampaignQuoteTemplateItem',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.campaignQuoteTemplateItems)

  return {
    callbacks: {
      createCampaignQuoteTemplateItem: (
        campaignQuoteTemplateItemParams: Partial<CampaignQuoteTemplateItemModel>,
        entityOptions?: CampaignQuoteTemplateItemRequestOptions,
      ) => (
        createCampaignQuoteTemplateItem({ campaignQuoteTemplateItemParams, dispatch, requestOptions: entityOptions })
      ),
      createCampaignQuoteTemplateItems: (
        orderType: 'bundle' | 'publication',
        itemPayload: CreateMultipleCampaignQuoteTemplateItemsPayload,
        entityOptions?: CampaignQuoteTemplateItemRequestOptions,
      ) => (
        createCampaignQuoteTemplateItems({
          itemPayload, dispatch, orderType, requestOptions: entityOptions,
        })
      ),
      createOrEditCampaignQuoteTemplateItem: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCampaignQuoteTemplateItemModal',
        payload: { campaignQuoteTemplateItem },
      }),
      deleteCampaignQuoteTemplateItem: () => deleteCampaignQuoteTemplateItem({ dispatch, campaignQuoteTemplateItem }),
      duplicateCampaignQuoteTemplateItem: (entityOptions?: CampaignQuoteTemplateItemRequestOptions) => (
        duplicateCampaignQuoteTemplateItem({
          campaignQuoteTemplateItem, dispatch, requestOptions: entityOptions,
        })
      ),
      editQuantityAndOptions: () => launchModal({
        callbacks,
        modalKey: 'EditItemOptionsModal',
        payload: {
          callbacks: {
            updateItem: (
              itemPayload: Partial<CampaignQuoteTemplateItemModel>,
              entityOptions?: CampaignQuoteTemplateItemRequestOptions,
            ) => updateCampaignQuoteTemplateItemOptions({
              dispatch,
              itemPayload,
              campaignQuoteTemplateItem,
              requestOptions: entityOptions,
            }),
          },
          entity: campaignQuoteTemplateItem,
        },
      }),
      launchPriceAutoUpdatedModal: () => launchModal({
        callbacks: {
          ...callbacks,
          updateCampaignQuoteTemplateItem: (
            campaignQuoteTemplateItemParams: Partial<CampaignQuoteTemplateItemModel>,
            entityOptions?: CampaignQuoteTemplateItemRequestOptions,
          ) => (
            updateCampaignQuoteTemplateItem({
              campaignQuoteTemplateItem, campaignQuoteTemplateItemParams, dispatch, requestOptions: entityOptions,
            })
          ),
        },
        modalKey: 'PriceAutoUpdatedModal',
        payload: { campaignQuoteTemplateItem },
      }),
      toggleCampaignQuoteTemplateItemSelection: (
        e: React.MouseEvent<HTMLAnchorElement>,
        entityOptions?: CampaignQuoteTemplateItemRequestOptions,
      ) => {
        e.stopPropagation()
        return updateCampaignQuoteTemplateItem({
          campaignQuoteTemplateItem,
          campaignQuoteTemplateItemParams: { selected: !campaignQuoteTemplateItem.selected },
          dispatch,
          requestOptions: entityOptions,
        })
      },
      updateCampaignQuoteTemplateItem: (
        campaignQuoteTemplateItemParams: Partial<CampaignQuoteTemplateItemModel>,
        entityOptions?: CampaignQuoteTemplateItemRequestOptions,
      ) => (
        updateCampaignQuoteTemplateItem({
          campaignQuoteTemplateItem, campaignQuoteTemplateItemParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    campaignQuoteTemplate,
    campaignQuoteTemplateItem,
    creating,
    deleting,
    loading: loading && !loadedIds.includes(campaignQuoteTemplateItem.id),
    updating,
    urls: generateUrls(campaignQuoteTemplateItem),
  }
}

export default useCampaignQuoteTemplateItem
